import { useEffect } from "react";
import { message } from "antd";

import { controller } from "../../Login/controller";
import { checkoutOutcome, pendingOfficeIds, PENDING_SUBSCRIBE_KEY, pollWindowMs } from "./checkoutCart";
import { FINAL_CHARGE_STATES } from "./subscribeErrors";
import {
  clearHelcimReturn,
  helcimAttachAlreadyDone,
  markHelcimAttachDone,
  readHelcimReturn,
} from "./helcimReturn";

/**
 * Finish a subscription purchase after Helcim's card round trip.
 *
 * ONLY a subscription purchase. This hook is mounted at the app root, so it
 * runs on every Helcim return — and the patient billing features (a separate
 * integration: /billpay/*, Paymentcontroller.js, a dozen page-level readers)
 * come back through the very same URL parameters, some on public pages a
 * patient reaches from an emailed link. The `pendingSubscribe` key is what says
 * "this return is ours": the two subscription checkouts write it, nothing else
 * does. Without it this hook does nothing at all — no attach, no toast, no URL
 * cleanup — so a patient payment is never touched. (It was briefly removed on
 * 2026-09-16 on the theory that a lost key shouldn't strand a purchase; that
 * made every patient-payment return call the subscription attach with the
 * patient's token. A stranded purchase is recoverable; that was not.)
 *
 * The attach endpoint answers 201 when the card is STORED. The first charge is
 * taken just after, off the request thread, so this polls the purchase status
 * and speaks from it — never "active" from the 201 alone.
 */

const POLL_EVERY_MS = 1000;

/** Whether this return is ours to handle at all. Pure, for tests. */
export function shouldHandleReturn(present, pendingRaw) {
  if (!present) return false;
  if (!pendingRaw) return false;
  return pendingOfficeIds(pendingRaw).length > 0;
}

/**
 * Poll every office of the checkout until each has a final answer or the window
 * closes. Returns {officeId: state} — a still-running office reads "timeout".
 * Also used by the in-modal saved-card checkout.
 */
export async function pollCheckout(officeIds, isCancelled = () => false) {
  const deadline = Date.now() + pollWindowMs(officeIds.length);
  const states = {};
  officeIds.forEach((id) => { states[id] = "timeout"; });
  while (Date.now() < deadline) {
    if (isCancelled()) return null;
    try {
      const res = await controller.getCheckoutStatus(officeIds);
      if (res && res.status === 200 && res.offices) {
        officeIds.forEach((id) => {
          const row = res.offices[String(id)];
          if (row && row.state) states[id] = FINAL_CHARGE_STATES.includes(row.state) ? row.state : "timeout";
        });
        if (officeIds.every((id) => FINAL_CHARGE_STATES.includes(states[id]))) return states;
      }
    } catch (e) {
      // keep polling; the network blip is not the outcome
    }
    await new Promise((r) => setTimeout(r, POLL_EVERY_MS));
  }
  return states;
}

export default function useHelcimReturn() {
  useEffect(() => {
    const { present, approved, cardToken, responseMessage, noticeMessage, from } =
      readHelcimReturn();
    const pendingRaw = localStorage.getItem(PENDING_SUBSCRIBE_KEY);

    // Not a subscription purchase (an ordinary page load, or a patient
    // payment return, which has its own readers). Touch nothing.
    if (!shouldHandleReturn(present, pendingRaw)) return;
    const officeIds = pendingOfficeIds(pendingRaw);

    if (!approved) {
      console.warn("[helcim] card not approved:", responseMessage, noticeMessage);
      message.error(
        noticeMessage || responseMessage
          ? `Your card wasn't accepted: ${noticeMessage || responseMessage}`
          : "Your card wasn't accepted. Please try another card."
      );
      clearHelcimReturn(from); // keep the key: they may try another card
      return;
    }

    if (!cardToken) {
      console.error("[helcim] approved with no cardToken in the return URL:", window.location.href);
      message.error(
        "Your card was accepted but we didn't get the confirmation we need to finish. Please contact support before trying again."
      );
      clearHelcimReturn(from);
      return;
    }

    if (helcimAttachAlreadyDone(cardToken, pendingRaw)) {
      // This exact purchase has already been attached; a refresh brought us
      // back. Not an error, but say so rather than leaving a blank screen.
      console.info("[helcim] this purchase was already submitted; not attaching again");
      message.info("We already have this payment. Refreshing your plan…", 4);
      clearHelcimReturn(from);
      setTimeout(() => window.location.reload(), 1500);
      return;
    }
    markHelcimAttachDone(cardToken, pendingRaw);

    let cancelled = false;
    const isCancelled = () => cancelled;
    (async () => {
      try {
        const res = await controller.helcimPayMulti(cardToken);
        if (cancelled) return;
        if (!(res && res.status < 250)) {
          // The card was accepted by Helcim; it's the attach that failed, so say
          // so rather than implying the payment bounced.
          message.error(
            (res && (res.detail || res.error)) ||
              "Your card was accepted but we couldn't activate the subscription. Please contact support."
          );
          return;
        }
        const hide = message.loading("Confirming your payment…", 0);
        const states = await pollCheckout(officeIds, isCancelled);
        hide();
        if (cancelled || !states) return;
        const out = checkoutOutcome(states);
        if (out.kind === "success") {
          message.success(out.text);
          localStorage.removeItem(PENDING_SUBSCRIBE_KEY);
          // The trial banner and the request gate both read from the server.
          setTimeout(() => window.location.reload(), 1200);
        } else if (out.kind === "error") {
          message.error(out.text, 8); // the key stays: try another card
        } else {
          message.info(out.text, 10);
        }
      } catch (e) {
        if (!cancelled) {
          message.error(
            "Your card was accepted but we couldn't activate the subscription. Please contact support."
          );
        }
      } finally {
        clearHelcimReturn(from);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
